import { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "./lang";

// day index: 0 = Monday … 6 = Sunday; null = closed
export const HOURS = [["10:00", "21:00"], ["10:00", "21:00"], ["10:00", "21:00"], ["10:00", "21:00"], ["10:00", "21:00"], ["10:00", "21:00"], null];

const DAYS = {
  sv: ["Måndag", "Tisdag", "Onsdag", "Torsdag", "Fredag", "Lördag", "Söndag"],
  en: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
};
const WD = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
const toMin = (s) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));
const DAY_MS = 86400000;

let cache = [];
let loaded = null;
const listeners = new Set();

export const refreshSpecials = () => {
  const base = process.env.REACT_APP_BACKEND_URL;
  if (!base) return Promise.resolve([]);
  loaded = axios.get(`${base}/api/hours/specials`)
    .then((r) => { cache = Array.isArray(r.data) ? r.data : []; })
    .catch(() => { cache = []; })
    .finally(() => listeners.forEach((fn) => fn(cache)));
  return loaded;
};

const useSpecials = () => {
  const [list, setList] = useState(cache);
  useEffect(() => {
    listeners.add(setList);
    if (!loaded) refreshSpecials();
    return () => { listeners.delete(setList); };
  }, []);
  return list;
};

const stockholmNow = () => {
  const d = new Date();
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Stockholm", weekday: "short", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value;
  const iso = `${get("year")}-${get("month")}-${get("day")}`;
  return { iso, day: WD[get("weekday")], mins: (Number(get("hour")) % 24) * 60 + Number(get("minute")) };
};

const addDays = (iso, n) => new Date(Date.parse(`${iso}T12:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);

// returns { hours: [open, close] | null, special }
const hoursFor = (iso, day, specials) => {
  const sp = specials.find((s) => s.date === iso);
  if (sp) return { hours: sp.closed ? null : [sp.open, sp.close], special: sp };
  return { hours: HOURS[day], special: null };
};

const fmtDate = (iso, lang) => new Date(`${iso}T12:00:00Z`).toLocaleDateString(lang === "sv" ? "sv-SE" : "en-GB", { day: "numeric", month: "short", timeZone: "UTC" });

export const useOpenStatus = () => {
  const { lang } = useLang();
  const specials = useSpecials();
  const [now, setNow] = useState(stockholmNow);
  useEffect(() => {
    const id = setInterval(() => setNow(stockholmNow()), 60000);
    return () => clearInterval(id);
  }, []);
  const sv = lang === "sv";
  const { hours: today } = hoursFor(now.iso, now.day, specials);
  const open = !!today && now.mins >= toMin(today[0]) && now.mins < toMin(today[1]);
  let label;
  if (open) label = sv ? `Öppet nu · till ${today[1]}` : `Open now · until ${today[1]}`;
  else if (today && now.mins < toMin(today[0])) label = sv ? `Stängt · öppnar ${today[0]}` : `Closed · opens ${today[0]}`;
  else {
    let found = null;
    for (let i = 1; i <= 21 && !found; i += 1) {
      const d = (now.day + i) % 7;
      const h = hoursFor(addDays(now.iso, i), d, specials).hours;
      if (h) found = { i, d, h };
    }
    if (!found) label = sv ? "Stängt" : "Closed";
    else {
      const when = found.i === 1 ? (sv ? "i morgon" : "tomorrow") : sv ? DAYS.sv[found.d].toLowerCase() : DAYS.en[found.d];
      label = sv ? `Stängt · öppnar ${when} ${found.h[0]}` : `Closed · opens ${when} ${found.h[0]}`;
    }
  }
  return { open, label, today: now.day, iso: now.iso, specials };
};

export const useWeek = () => {
  const { lang } = useLang();
  const { today, iso, specials } = useOpenStatus();
  const monday = addDays(iso, -today);
  const rows = DAYS[lang].map((name, i) => {
    const date = addDays(monday, i);
    const { hours, special } = hoursFor(date, i, specials);
    return { name, date, special, isToday: i === today, text: hours ? `${hours[0]}–${hours[1]}` : lang === "sv" ? "Stängt" : "Closed" };
  });
  const limit = addDays(iso, 14);
  const upcoming = specials
    .filter((s) => s.date >= iso && s.date <= limit)
    .map((s) => ({ ...s, label: fmtDate(s.date, lang), text: s.closed ? (lang === "sv" ? "Stängt" : "Closed") : `${s.open}–${s.close}` }));
  return { rows, upcoming };
};
